import React, { useMemo } from "react";
import { View, StyleSheet } from "react-native";
import { useNavigation } from "@react-navigation/native";
import {
  FertilityTestingStackScreenProps,
  type FertilityTestingStackParamList,
} from "../FertilityTestingNavigator";
import {
  QuestionLayout,
  SquareOptionButton,
} from "@src/packages/medical-history/components";
import {
  Monitor,
  ScanLine,
  Stethoscope,
  Activity,
  Brain,
  Camera,
  Scan,
  Disc,
  Sparkles,
  type LucideIcon,
} from "lucide-react-native";
import type {
  TestResultCreate,
  ImagingTestCategory,
} from "@louise/medical-history/basic-info/types";
import { useTranslations } from "@src/packages/i18n";
import { useFertilityTesting } from "../FertilityTestingContext";
import { getNextTestScreen } from "../getNextTestScreen";
import { useFertilityTestingUpload } from "../useFertilityTestingUpload";
import { isFertilityTestingResult } from "../isFertilityTestingResult";

type ImagingTestType =
  | "transabdominal"
  | "transvaginal"
  | "hysterosonography"
  | "hysterosalpingography"
  | "antral_follicle"
  | "laparoscopy"
  | "hysteroscopy"
  | "pelvic_mri"
  | "other";

const imagingTests: {
  id: ImagingTestType;
  labelKey: string;
  icon: LucideIcon;
  sex: "AFAB" | "AMAB" | "both";
}[] = [
  {
    id: "transabdominal",
    labelKey: "med_history.fertility.testing.imaging_type.transabdominal",
    icon: Monitor,
    sex: "both",
  },
  {
    id: "transvaginal",
    labelKey: "med_history.fertility.testing.imaging_type.transvaginal",
    icon: ScanLine,
    sex: "AFAB",
  },
  {
    id: "hysterosonography",
    labelKey: "med_history.fertility.testing.imaging_type.hysterosonography",
    icon: Stethoscope,
    sex: "AFAB",
  },
  {
    id: "hysterosalpingography",
    labelKey:
      "med_history.fertility.testing.imaging_type.hysterosalpingography",
    icon: Activity,
    sex: "AFAB",
  },
  {
    id: "antral_follicle",
    labelKey: "med_history.fertility.testing.imaging_type.antral_follicle",
    icon: Brain,
    sex: "AFAB",
  },
  {
    id: "laparoscopy",
    labelKey: "med_history.fertility.testing.imaging_type.laparoscopy",
    icon: Camera,
    sex: "AFAB",
  },
  {
    id: "hysteroscopy",
    labelKey: "med_history.fertility.testing.imaging_type.hysteroscopy",
    icon: Scan,
    sex: "AFAB",
  },
  {
    id: "pelvic_mri",
    labelKey: "med_history.fertility.testing.imaging_type.pelvic_mri",
    icon: Disc,
    sex: "AFAB",
  },
  {
    id: "other",
    labelKey: "med_history.fertility.testing.imaging_type.other",
    icon: Sparkles,
    sex: "both",
  },
];

const categoryToUI: Record<string, ImagingTestType> = {
  transabdominal_ultrasound: "transabdominal",
  transvaginal_ultrasound: "transvaginal",
  hysterosonography: "hysterosonography",
  hysterosalpingography: "hysterosalpingography",
  antral_follicle_count: "antral_follicle",
  laparoscopy: "laparoscopy",
  hysteroscopy: "hysteroscopy",
  pelvic_mri: "pelvic_mri",
  other: "other",
};

export function ImagingTestsScreen() {
  const t = useTranslations();
  const { state, updateState } = useFertilityTesting();
  const navigation =
    useNavigation<
      FertilityTestingStackScreenProps<"ImagingTests">["navigation"]
    >();
  const { uploadAction, uploadModal, uploadedFiles } =
    useFertilityTestingUpload();

  const imagingResults = state.testResults.filter(
    (tr) => tr.type === "imaging" && isFertilityTestingResult(tr),
  );

  const selected = useMemo<Set<ImagingTestType>>(() => {
    const selection = new Set<ImagingTestType>();
    imagingResults.forEach((result) => {
      const uiType = categoryToUI[result.imagingTestCategory ?? ""];
      if (uiType) {
        selection.add(uiType);
      }
    });
    return selection;
  }, [imagingResults]);

  const handleSelectionChange = (newSelection: ImagingTestType[]) => {
    const categoryMap: Record<ImagingTestType, string> = {
      transabdominal: "transabdominal_ultrasound",
      transvaginal: "transvaginal_ultrasound",
      hysterosonography: "hysterosonography",
      hysterosalpingography: "hysterosalpingography",
      antral_follicle: "antral_follicle_count",
      laparoscopy: "laparoscopy",
      hysteroscopy: "hysteroscopy",
      pelvic_mri: "pelvic_mri",
      other: "other",
    };
    const newSelectionSet = new Set(newSelection);

    const oldSelection = new Set<ImagingTestType>();
    imagingResults.forEach((result) => {
      const uiType = categoryToUI[result.imagingTestCategory ?? ""];
      if (uiType) {
        oldSelection.add(uiType);
      }
    });

    const removedIds = imagingResults
      .filter((result) => {
        const uiType = categoryToUI[result.imagingTestCategory ?? ""];
        return result.id && uiType && !newSelectionSet.has(uiType);
      })
      .map((result) => result.id!);

    let updatedResults = state.testResults.filter((result) => {
      if (result.type !== "imaging" || !isFertilityTestingResult(result)) {
        return true;
      }
      const uiType = categoryToUI[result.imagingTestCategory ?? ""];
      return uiType ? newSelectionSet.has(uiType) : true;
    });

    newSelection.forEach((testType) => {
      if (!oldSelection.has(testType)) {
        const newTestResult: TestResultCreate = {
          type: "imaging",
          imagingTestCategory: categoryMap[testType] as ImagingTestCategory,
        };
        updatedResults = [...updatedResults, newTestResult];
      }
    });

    updateState({
      testResults: updatedResults,
      testResultIdsToDelete: Array.from(
        new Set([...state.testResultIdsToDelete, ...removedIds]),
      ),
    });
  };

  const handleToggle = (testType: ImagingTestType) => {
    const nextSelection = new Set(selected);
    if (nextSelection.has(testType)) {
      nextSelection.delete(testType);
    } else {
      nextSelection.add(testType);
    }
    handleSelectionChange(Array.from(nextSelection));
  };

  const handleNext = () => {
    const newIndex = (state.currentNavigationIndex || 0) + 1;
    updateState({ currentNavigationIndex: newIndex });
    const nextScreen = getNextTestScreen({
      ...state,
      currentNavigationIndex: newIndex,
    });
    navigation.navigate(nextScreen as keyof FertilityTestingStackParamList);
  };

  return (
    <>
      <QuestionLayout
        title={t("med_history.fertility.testing.imaging_title")}
        subtitle={t("med_history.fertility.testing.imaging_subtitle")}
        onBack={() => navigation.goBack()}
        onSave={handleNext}
        saveDisabled={selected.size === 0}
        saveLabel={t("med_history.fertility.continue")}
        secondaryAction={uploadAction}
        uploadedFiles={uploadedFiles}
      >
        <View style={styles.optionsGrid}>
          {imagingTests
            .filter((test) => test.sex === "both" || test.sex === state.sex)
            .map((test) => {
              const isSelected = selected.has(test.id);
              return (
                <View key={test.id} style={styles.optionWrapper}>
                  <SquareOptionButton
                    icon={test.icon}
                    label={t(test.labelKey)}
                    isSelected={isSelected}
                    onPress={() => handleToggle(test.id)}
                    iconSize={20}
                    containerSize={40}
                    labelSize="sm"
                  />
                </View>
              );
            })}
        </View>
      </QuestionLayout>
      {uploadModal}
    </>
  );
}

const styles = StyleSheet.create({
  optionsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 40,
    width: "100%",
  },
  optionWrapper: {
    width: "47%",
    minWidth: "47%",
  },
});

export default ImagingTestsScreen;
